import { motion } from "framer-motion";
import {
  Factory,
  ShoppingBag,
  Building2,
  Landmark,
  Cloud,
  Cog,
} from "lucide-react";
import { SectionHeader } from "@/components/GlobalTradingNetwork";
import { easeExpo } from "@/lib/motion";

const INDUSTRIES = [
  {
    key: "oem",
    label: "OEM",
    icon: Factory,
    blurb: "Assembly-grade components for design-in and production runs.",
  },
  {
    key: "retail",
    label: "Retail",
    icon: ShoppingBag,
    blurb: "Distribution-ready SKUs for channel and e-tail resellers.",
  },
  {
    key: "enterprise",
    label: "Enterprise",
    icon: Building2,
    blurb: "Server, storage and network refresh cycles at scale.",
  },
  {
    key: "government",
    label: "Government",
    icon: Landmark,
    blurb: "Compliance-first sourcing for public sector programs.",
  },
  {
    key: "cloud",
    label: "Cloud",
    icon: Cloud,
    blurb: "Hyperscale-grade GPU, DRAM and NVMe fulfilment.",
  },
  {
    key: "industrial",
    label: "Industrial Automation",
    icon: Cog,
    blurb: "Long-lifecycle silicon for factories and utilities.",
  },
];

export default function IndustriesServed() {
  return (
    <section
      id="industries"
      className="relative py-28 md:py-40 border-t border-white/5"
      data-testid="section-industries"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <SectionHeader
          index="04"
          overline="Industries Served"
          title={
            <>
              Silicon for the <br />
              teams that <span className="text-electric-500">build</span>.
            </>
          }
          copy="From OEM production lines in Shenzhen to public-sector fleets in Frankfurt — six categories of buyers rely on OmniCoreTek for continuity of supply."
        />

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/5">
          {INDUSTRIES.map((it, i) => (
            <motion.div
              key={it.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: easeExpo, delay: (i % 3) * 0.05 }}
              className="relative p-8 md:p-10 border-b border-r border-white/5 min-h-[240px] group overflow-hidden"
              data-testid={`industry-tile-${it.key}`}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 0% 0%, rgba(0,229,255,0.08), transparent 60%)",
                  transition: "opacity 500ms cubic-bezier(0.16,1,0.3,1)",
                }}
              />
              <div className="flex items-center justify-between">
                <it.icon
                  className="w-5 h-5 text-slate-400 group-hover:text-electric-500"
                  style={{ transition: "color 400ms" }}
                  strokeWidth={1.4}
                />
                <span className="text-[10px] font-mono tracking-[0.22em] uppercase text-slate-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="mt-14">
                <div className="font-display text-3xl md:text-4xl tracking-tight text-white">
                  {it.label}
                </div>
                <p className="mt-3 text-slate-400 text-sm max-w-[320px] leading-relaxed">
                  {it.blurb}
                </p>
              </div>
              {/* Corner tick */}
              <div className="absolute bottom-4 right-4 w-3 h-3 border-b border-r border-white/10 group-hover:border-electric-500/50" style={{ transition: "border-color 400ms" }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
